import React, { useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  addMinutes,
} from 'date-fns';
import { cn } from '@/lib/utils';
import { MessageCircle, Video, Building2, X, CalendarDays, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { EmrAppointment } from '@/services/emrService';
import VisitActionButton from '@/components/visit/VisitActionButton';
import type { CalendarEvent } from '@/services/calendarService';
import type { Task } from '@/services/taskService';

interface MonthViewProps {
  currentMonth: Date;
  appointments: EmrAppointment[];
  externalEvents?: CalendarEvent[];
  tasks?: Task[];
  onAppointmentClick: (appointment: EmrAppointment) => void;
  onDayClick: (date: Date) => void;
  onTaskClick?: (task: Task) => void;
  privacyMode: 'names' | 'initials';
}

const taskPriorityColors: Record<string, string> = {
  high: '#ef4444',
  medium: '#f97316',
  low: '#eab308',
};

const TaskPill: React.FC<{ task: Task; onTaskClick?: (task: Task) => void; className?: string }> = ({
  task,
  onTaskClick,
  className,
}) => {
  const color = taskPriorityColors[task.priority] || '#f97316';
  const isCompleted = (task.effective_status || task.status) === 'completed';
  return (
    <div
      className={cn(
        'cursor-pointer rounded-md px-1.5 py-0.5 text-[10px] transition-all hover:opacity-80 sm:px-2 sm:py-1 sm:text-xs',
        isCompleted && 'opacity-60',
        className
      )}
      style={{
        backgroundColor: '#f9731615',
        borderLeft: `3px solid ${color}`,
      }}
      onClick={e => {
        e.stopPropagation();
        onTaskClick?.(task);
      }}
    >
      <div className="flex items-center gap-0.5 sm:gap-1">
        <ClipboardList className="h-2.5 w-2.5 flex-shrink-0 text-orange-500 sm:h-3 sm:w-3" />
        <span className={cn('flex-1 truncate font-medium text-slate-700', isCompleted && 'line-through')}>
          {task.title}
        </span>
      </div>
    </div>
  );
};

// Check if appointment is joinable (within 15 min before to 30 min after start)
const isJoinable = (
  apt: EmrAppointment,
  isPractitioner: boolean,
  userPractitionerId?: string
): boolean => {
  if (!isPractitioner) return false;
  const isVideoAppointment = (apt as { visit_mode?: string }).visit_mode === 'video';
  if (!isVideoAppointment) return false;
  if (apt.status === 'completed' || apt.status === 'cancelled') return false;
  if (userPractitionerId && apt.practitioner_id !== userPractitionerId) return false;

  const now = new Date();
  const start = new Date(apt.start_time);
  const windowStart = addMinutes(start, -15);
  const windowEnd = addMinutes(start, apt.duration || 30);

  return isWithinInterval(now, { start: windowStart, end: windowEnd });
};

const toInitials = (full: string) =>
  full
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => (s[0] ? s[0].toUpperCase() + '.' : ''))
    .join('');

const statusColors: Record<string, string> = {
  scheduled: '#3b82f6',
  confirmed: '#22c55e',
  checked_in: '#f59e0b',
  in_progress: '#8b5cf6',
  completed: '#6b7280',
  no_show: '#ef4444',
  cancelled: '#9ca3af',
};

const statusLabels: Record<string, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  checked_in: 'Checked In',
  in_progress: 'In Progress',
  completed: 'Completed',
  no_show: 'No Show',
  cancelled: 'Cancelled',
};

interface AppointmentPillProps {
  apt: EmrAppointment;
  displayLabel: string;
  canJoin: boolean;
  onAppointmentClick: (apt: EmrAppointment) => void;
  onJoin: (e: React.MouseEvent, apt: EmrAppointment) => void;
  className?: string;
}

const AppointmentPill: React.FC<AppointmentPillProps> = ({
  apt,
  displayLabel,
  canJoin,
  onAppointmentClick,
  onJoin,
  className,
}) => {
  const color = apt.color || apt.serviceType?.color || statusColors[apt.status] || '#4A8A9A';
  const isCancelled = apt.status === 'cancelled';
  return (
    <div
      className={cn(
        'cursor-pointer rounded-md px-1.5 py-0.5 text-[10px] transition-all hover:opacity-80 sm:px-2 sm:py-1 sm:text-xs',
        isCancelled && 'opacity-60',
        canJoin && 'ring-2 ring-brand-teal ring-offset-1',
        className
      )}
      style={{
        backgroundColor: color + '15',
        borderLeft: `3px solid ${color}`,
      }}
      onClick={e => {
        e.stopPropagation();
        onAppointmentClick(apt);
      }}
    >
      <div className='flex items-center gap-0.5 sm:gap-1'>
        {(apt as { visit_mode?: string }).visit_mode === 'video' && (
          <Video className='h-2.5 w-2.5 flex-shrink-0 text-blue-500 sm:h-3 sm:w-3' />
        )}
        {(apt as { visit_mode?: string }).visit_mode === 'async_message' && (
          <MessageCircle className='h-2.5 w-2.5 flex-shrink-0 text-indigo-500 sm:h-3 sm:w-3' />
        )}
        {(apt as { visit_mode?: string }).visit_mode === 'in_person' && (
          <Building2 className='h-2.5 w-2.5 flex-shrink-0 text-brand-teal sm:h-3 sm:w-3' />
        )}
        <span className={cn('flex-1 truncate font-medium text-slate-700', isCancelled && 'line-through')}>
          <span className='hidden sm:inline'>
            {format(new Date(apt.start_time), 'h:mm a')}{' '}
          </span>
          <span className='sm:hidden'>
            {format(new Date(apt.start_time), 'h:mma').toLowerCase()}{' '}
          </span>
          {displayLabel}
        </span>
        {canJoin && (
          <button
            onClick={e => onJoin(e, apt)}
            className='flex-shrink-0 rounded bg-brand-teal px-1.5 py-0.5 text-[8px] font-bold text-white transition-colors hover:bg-brand-teal/90 sm:text-[10px]'
          >
            JOIN
          </button>
        )}
        <VisitActionButton
          appointment={apt}
          variant='icon'
          stopPropagation
          className='ml-0.5 hidden sm:inline-flex !h-5 !w-5'
        />
      </div>
    </div>
  );
};

const ExternalEventPill: React.FC<{ event: CalendarEvent }> = ({ event }) => {
  const isTentative = event.status === 'tentative';
  const displayTitle = event.title || (isTentative ? 'Tentative' : 'Busy');
  return (
    <div
      className={cn(
        'rounded-md px-1.5 py-0.5 text-[10px] sm:px-2 sm:py-1 sm:text-xs',
        isTentative ? 'bg-amber-50 border-l-[3px] border-amber-400' : 'bg-slate-100 border-l-[3px] border-slate-400',
      )}
    >
      <div className="flex items-center gap-0.5 sm:gap-1">
        <CalendarDays className={cn('h-2.5 w-2.5 flex-shrink-0 sm:h-3 sm:w-3', isTentative ? 'text-amber-500' : 'text-slate-400')} />
        <span className={cn('flex-1 truncate font-medium', isTentative ? 'text-amber-700' : 'text-slate-500')}>
          <span className="hidden sm:inline">
            {event.isAllDay ? 'All day' : format(new Date(event.start), 'h:mm a')}{' '}
          </span>
          <span className="sm:hidden">
            {event.isAllDay ? 'All day' : format(new Date(event.start), 'h:mma').toLowerCase()}{' '}
          </span>
          {displayTitle}
        </span>
      </div>
    </div>
  );
};

const MonthView: React.FC<MonthViewProps> = ({
  currentMonth,
  appointments,
  externalEvents = [],
  tasks = [],
  onAppointmentClick,
  onDayClick,
  onTaskClick,
  privacyMode,
}) => {
  const navigate = useNavigate();
  const { isPractitioner } = useClinic();
  const { user } = useAuth();

  // Track which day's overflow popover is open (by dateKey)
  const [openPopoverDate, setOpenPopoverDate] = useState<string | null>(null);

  const handleQuickJoin = (e: React.MouseEvent, apt: EmrAppointment) => {
    e.stopPropagation();
    if (isPractitioner) {
      navigate(`/practitioner-portal/telehealth/call/${apt.id}`);
    } else {
      // Admin + clinic_staff + everyone else lands at root.
      navigate(`/telehealth/call/${apt.id}`);
    }
  };

  const handleAppointmentClickFromPopover = (apt: EmrAppointment) => {
    setOpenPopoverDate(null);
    onAppointmentClick(apt);
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const appointmentsByDate = new Map<string, EmrAppointment[]>();
  appointments.forEach(apt => {
    const dateKey = format(new Date(apt.start_time), 'yyyy-MM-dd');
    if (!appointmentsByDate.has(dateKey)) {
      appointmentsByDate.set(dateKey, []);
    }
    appointmentsByDate.get(dateKey)!.push(apt);
  });

  const externalByDate = new Map<string, CalendarEvent[]>();
  externalEvents.filter(e => e.status !== 'free').forEach(evt => {
    const dateKey = format(new Date(evt.start), 'yyyy-MM-dd');
    if (!externalByDate.has(dateKey)) {
      externalByDate.set(dateKey, []);
    }
    externalByDate.get(dateKey)!.push(evt);
  });

  const tasksByDate = new Map<string, Task[]>();
  tasks.forEach(task => {
    const dateKey = task.due_date; // already YYYY-MM-DD
    if (!tasksByDate.has(dateKey)) {
      tasksByDate.set(dateKey, []);
    }
    tasksByDate.get(dateKey)!.push(task);
  });

  const today = new Date();

  return (
    <div className='flex h-full min-h-0 flex-col'>
      {/* Day headers */}
      <div className='sticky top-0 z-10 grid grid-cols-7 border-b border-slate-200' style={{ backgroundColor: '#f8f3ed' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div
            key={day}
            className='border-l border-slate-200 px-2 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-600 first:border-l-0'
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className='grid flex-1 grid-cols-7 overflow-y-auto'>
        {days.map((day, index) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayAppointments = appointmentsByDate.get(dateKey) || [];
          const dayExternalEvents = externalByDate.get(dateKey) || [];
          const dayTasks = tasksByDate.get(dateKey) || [];
          const totalItems = dayAppointments.length + dayExternalEvents.length + dayTasks.length;
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isToday = isSameDay(day, today);
          const hiddenCount = totalItems - 3;
          const mobileHiddenCount = totalItems - 2;

          return (
            <div
              key={index}
              className={cn(
                'min-h-[80px] cursor-pointer border-b border-l border-slate-100 p-1.5 transition-colors first:border-l-0 hover:bg-slate-50/50 sm:min-h-[100px] sm:p-2 md:min-h-[120px]',
                !isCurrentMonth && 'bg-slate-50/40'
              )}
              onClick={() => onDayClick(day)}
            >
              {/* Day number */}
              <div
                className={cn(
                  'mb-1 text-xs font-medium text-slate-700 sm:text-sm',
                  isToday &&
                    'inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-teal font-semibold text-white sm:h-7 sm:w-7',
                  !isCurrentMonth && !isToday && 'text-slate-400'
                )}
              >
                {format(day, 'd')}
              </div>

              {/* Appointments */}
              <div className='space-y-0.5 sm:space-y-1'>
                {/* Render appointments first, then external events, up to visible limit */}
                {(() => {
                  const visibleDesktop = 3;
                  const allItems: React.ReactNode[] = [];
                  let rendered = 0;

                  // Tasks first (as banners at top)
                  for (const task of dayTasks) {
                    if (rendered >= visibleDesktop) break;
                    allItems.push(
                      <TaskPill
                        key={`task-${task.task_id}`}
                        task={task}
                        onTaskClick={onTaskClick}
                        className={rendered === 2 ? 'hidden sm:block' : undefined}
                      />
                    );
                    rendered++;
                  }

                  // Appointments
                  for (const apt of dayAppointments) {
                    if (rendered >= visibleDesktop) break;
                    const patientName =
                      apt.patient_name ||
                      (apt.patient
                        ? `${apt.patient.first_name} ${apt.patient.last_name}`
                        : 'Unknown');
                    const displayLabel =
                      privacyMode === 'initials' ? toInitials(patientName) : patientName;
                    const canJoin = isJoinable(apt, isPractitioner, user?.practitionerId);

                    allItems.push(
                      <AppointmentPill
                        key={apt.id}
                        apt={apt}
                        displayLabel={displayLabel}
                        canJoin={canJoin}
                        onAppointmentClick={onAppointmentClick}
                        onJoin={handleQuickJoin}
                        className={rendered === 2 ? 'hidden sm:block' : undefined}
                      />
                    );
                    rendered++;
                  }

                  // External events fill remaining slots
                  for (const evt of dayExternalEvents) {
                    if (rendered >= visibleDesktop) break;
                    allItems.push(
                      <ExternalEventPill
                        key={`ext-${evt.id}`}
                        event={evt}
                      />
                    );
                    rendered++;
                  }

                  return allItems;
                })()}

                {/* +N more — desktop (> 3 shown) */}
                {hiddenCount > 0 && (
                  <Popover
                    open={openPopoverDate === dateKey + '-desktop'}
                    onOpenChange={open =>
                      setOpenPopoverDate(open ? dateKey + '-desktop' : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <button
                        className='hidden w-full px-1.5 text-left text-[10px] font-medium text-slate-500 hover:text-teal-600 sm:block sm:px-2 sm:text-xs'
                        onClick={e => e.stopPropagation()}
                      >
                        +{hiddenCount} more
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className='w-72 p-0'
                      side='right'
                      align='start'
                      onClick={e => e.stopPropagation()}
                    >
                      <OverflowPopoverContent
                        day={day}
                        appointments={dayAppointments}
                        externalEvents={dayExternalEvents}
                        tasks={dayTasks}
                        privacyMode={privacyMode}
                        isPractitioner={isPractitioner}
                        userPractitionerId={user?.practitionerId}
                        onAppointmentClick={handleAppointmentClickFromPopover}
                        onJoin={handleQuickJoin}
                        onTaskClick={onTaskClick}
                        onClose={() => setOpenPopoverDate(null)}
                      />
                    </PopoverContent>
                  </Popover>
                )}

                {/* +N more — mobile (> 2 shown) */}
                {mobileHiddenCount > 0 && (
                  <Popover
                    open={openPopoverDate === dateKey + '-mobile'}
                    onOpenChange={open =>
                      setOpenPopoverDate(open ? dateKey + '-mobile' : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <button
                        className='w-full px-1.5 text-left text-[10px] font-medium text-slate-500 hover:text-teal-600 sm:hidden'
                        onClick={e => e.stopPropagation()}
                      >
                        +{mobileHiddenCount} more
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className='w-72 p-0'
                      side='right'
                      align='start'
                      onClick={e => e.stopPropagation()}
                    >
                      <OverflowPopoverContent
                        day={day}
                        appointments={dayAppointments}
                        externalEvents={dayExternalEvents}
                        tasks={dayTasks}
                        privacyMode={privacyMode}
                        isPractitioner={isPractitioner}
                        userPractitionerId={user?.practitionerId}
                        onAppointmentClick={handleAppointmentClickFromPopover}
                        onJoin={handleQuickJoin}
                        onTaskClick={onTaskClick}
                        onClose={() => setOpenPopoverDate(null)}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Overflow Popover Content ---

interface OverflowPopoverContentProps {
  day: Date;
  appointments: EmrAppointment[];
  externalEvents?: CalendarEvent[];
  tasks?: Task[];
  privacyMode: 'names' | 'initials';
  isPractitioner: boolean;
  userPractitionerId?: string;
  onAppointmentClick: (apt: EmrAppointment) => void;
  onJoin: (e: React.MouseEvent, apt: EmrAppointment) => void;
  onTaskClick?: (task: Task) => void;
  onClose: () => void;
}

const OverflowPopoverContent: React.FC<OverflowPopoverContentProps> = ({
  day,
  appointments,
  externalEvents = [],
  tasks = [],
  privacyMode,
  isPractitioner,
  userPractitionerId,
  onAppointmentClick,
  onJoin,
  onTaskClick,
  onClose,
}) => {
  const totalCount = appointments.length + externalEvents.length + tasks.length;
  return (
    <div className='flex flex-col max-h-80'>
      {/* Header */}
      <div className='flex items-center justify-between border-b border-slate-100 px-3 py-2.5'>
        <div>
          <p className='text-xs font-semibold text-slate-900'>
            {format(day, 'EEEE')}
          </p>
          <p className='text-xs text-slate-500'>
            {format(day, 'MMMM d, yyyy')} · {totalCount} event{totalCount !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={onClose}
          className='rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600'
        >
          <X className='h-3.5 w-3.5' />
        </button>
      </div>

      {/* Appointment list */}
      <div className='overflow-y-auto p-2 space-y-1.5'>
        {appointments.map(apt => {
          const patientName =
            apt.patient_name ||
            (apt.patient
              ? `${apt.patient.first_name} ${apt.patient.last_name}`
              : 'Unknown');
          const displayLabel =
            privacyMode === 'initials' ? toInitials(patientName) : patientName;
          const canJoin = isJoinable(apt, isPractitioner, userPractitionerId);
          const color = apt.color || apt.serviceType?.color || statusColors[apt.status] || '#4A8A9A';

          return (
            <div
              key={apt.id}
              className='cursor-pointer rounded-lg px-3 py-2 transition-colors hover:bg-slate-50'
              style={{ borderLeft: `3px solid ${color}` }}
              onClick={() => onAppointmentClick(apt)}
            >
              <div className='flex items-start justify-between gap-2'>
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-1 mb-0.5'>
                    {(apt as { visit_mode?: string }).visit_mode === 'video' && (
                      <Video className='h-3 w-3 flex-shrink-0 text-blue-500' />
                    )}
                    {(apt as { visit_mode?: string }).visit_mode === 'async_message' && (
                      <MessageCircle className='h-3 w-3 flex-shrink-0 text-indigo-500' />
                    )}
                    {(apt as { visit_mode?: string }).visit_mode === 'in_person' && (
                      <Building2 className='h-3 w-3 flex-shrink-0 text-brand-teal' />
                    )}
                    <span className='text-xs font-semibold text-slate-800 truncate'>
                      {format(new Date(apt.start_time), 'h:mm a')}
                    </span>
                  </div>
                  <p className='text-xs text-slate-700 truncate font-medium'>{displayLabel}</p>
                  {apt.service_type_name && (
                    <p className='text-[10px] text-slate-500 truncate mt-0.5'>{apt.service_type_name}</p>
                  )}
                </div>
                <div className='flex flex-col items-end gap-1 flex-shrink-0'>
                  <span
                    className='rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide'
                    style={{ backgroundColor: color + '20', color }}
                  >
                    {statusLabels[apt.status] || apt.status}
                  </span>
                  {canJoin && (
                    <button
                      onClick={e => onJoin(e, apt)}
                      className='rounded bg-brand-teal px-2 py-0.5 text-[9px] font-bold text-white transition-colors hover:bg-brand-teal/90'
                    >
                      JOIN
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Tasks */}
        {tasks.length > 0 && (
          <>
            {appointments.length > 0 && (
              <div className='flex items-center gap-2 px-1 pt-1'>
                <div className='h-px flex-1 bg-orange-100' />
                <span className='text-[9px] font-medium uppercase tracking-wider text-orange-400'>Tasks</span>
                <div className='h-px flex-1 bg-orange-100' />
              </div>
            )}
            {tasks.map(task => {
              const priorityColor = taskPriorityColors[task.priority] || '#f97316';
              const isCompleted = (task.effective_status || task.status) === 'completed';
              return (
                <div
                  key={`task-${task.task_id}`}
                  className='cursor-pointer rounded-lg px-3 py-2 transition-colors hover:bg-orange-50/50'
                  style={{ borderLeft: `3px solid ${priorityColor}` }}
                  onClick={() => onTaskClick?.(task)}
                >
                  <div className='flex items-start justify-between gap-2'>
                    <div className='min-w-0 flex-1'>
                      <div className='flex items-center gap-1 mb-0.5'>
                        <ClipboardList className='h-3 w-3 flex-shrink-0 text-orange-500' />
                        <span className={cn('text-xs font-semibold text-slate-800 truncate', isCompleted && 'line-through')}>
                          {task.title}
                        </span>
                      </div>
                      {task.patient && (
                        <p className='text-[10px] text-slate-500 truncate mt-0.5'>
                          {[task.patient.first_name, task.patient.middle_name, task.patient.last_name].filter(Boolean).join(' ')}
                        </p>
                      )}
                    </div>
                    <div className='flex flex-col items-end gap-1 flex-shrink-0'>
                      <span
                        className='rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide'
                        style={{ backgroundColor: priorityColor + '20', color: priorityColor }}
                      >
                        {task.priority}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* External events */}
        {externalEvents.length > 0 && (
          <>
            {(appointments.length > 0 || tasks.length > 0) && (
              <div className='flex items-center gap-2 px-1 pt-1'>
                <div className='h-px flex-1 bg-slate-100' />
                <span className='text-[9px] font-medium uppercase tracking-wider text-slate-400'>External</span>
                <div className='h-px flex-1 bg-slate-100' />
              </div>
            )}
            {externalEvents.map(evt => {
              const isTentative = evt.status === 'tentative';
              const displayTitle = evt.title || (isTentative ? 'Tentative' : 'Busy');
              return (
                <div
                  key={`ext-${evt.id}`}
                  className={cn(
                    'rounded-lg px-3 py-2',
                    isTentative ? 'border-l-[3px] border-amber-400 bg-amber-50/50' : 'border-l-[3px] border-slate-400 bg-slate-50',
                  )}
                >
                  <div className='flex items-center gap-1.5'>
                    <CalendarDays className={cn('h-3 w-3 flex-shrink-0', isTentative ? 'text-amber-500' : 'text-slate-400')} />
                    <div className='min-w-0 flex-1'>
                      <span className={cn('text-xs font-semibold', isTentative ? 'text-amber-700' : 'text-slate-600')}>
                        {evt.isAllDay ? 'All day' : format(new Date(evt.start), 'h:mm a')}
                      </span>
                      <p className={cn('text-xs truncate', isTentative ? 'text-amber-600' : 'text-slate-500')}>
                        {displayTitle}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default MonthView;
